import * as THREE from 'three';

/**
 * FIKO STREAMING LOD SYSTEM
 * ==========================
 * Progressive geometry loading ve streaming LOD
 *
 * PROBLEM:
 * - Buyuk CAD dosyalari: Yukleme sirasinda UI donuyor
 * - Tum geometri memory'de: RAM sisiyor
 * - LOD degisiklikleri: Gorunur pop-in/pop-out
 *
 * COZUM:
 * - Streaming: Geometri parca parca yukleniyor
 * - On-demand LOD: Sadece gerekli LOD level'lar olusturuluyor
 * - Smooth transitions: LOD gecisleri gorunmez
 * - Memory budgeting: RAM limiti icinde kaliniyor
 *
 * HEDEFLER:
 * - <100ms initial render (placeholder ile)
 * - <1GB RAM for 50M triangle scenes
 * - Zero visible LOD pops
 * - 60 FPS during loading
 */

// ==================== TYPES ====================

export interface StreamingGeometryChunk {
  id: string;
  lodLevel: number;
  triangleCount: number;
  boundingBox: THREE.Box3;
  state: 'pending' | 'loading' | 'loaded' | 'disposed';
  geometry: THREE.BufferGeometry | null;
  priority: number;
  lastUsedTime: number;
  memoryBytes: number;
}

export interface LODLevel {
  level: number;
  distanceThreshold: number;
  triangleRatio: number; // 1.0 = full, 0.1 = 10% of triangles
  geometry: THREE.BufferGeometry | null;
  isLoaded: boolean;
  isLoading: boolean;
}

export interface StreamingObjectState {
  objectId: number;
  lodLevels: LODLevel[];
  currentLOD: number;
  targetLOD: number;
  transitionProgress: number; // 0-1 for smooth transition
  boundingSphere: THREE.Sphere;
  distanceToCamera: number;
  screenSpaceSize: number;
  priority: number;
}

export interface MemoryBudget {
  maxBytes: number;
  usedBytes: number;
  reservedBytes: number;
  peakBytes: number;
}

// ==================== GEOMETRY DECIMATOR ====================

/**
 * Fast geometry decimation for LOD generation
 * Uses vertex clustering for speed
 */
export class FikoGeometryDecimator {
  // Pre-allocated buffers
  private tempVec1: THREE.Vector3 = new THREE.Vector3();
  private tempVec2: THREE.Vector3 = new THREE.Vector3();
  private tempVec3: THREE.Vector3 = new THREE.Vector3();

  /**
   * Decimate geometry to target ratio
   * Uses vertex clustering - O(n) complexity
   */
  decimate(
    geometry: THREE.BufferGeometry,
    targetRatio: number
  ): THREE.BufferGeometry {
    if (targetRatio >= 1.0) {
      return geometry.clone();
    }

    const position = geometry.getAttribute('position');
    const originalVertexCount = position.count;
    const targetVertexCount = Math.max(3, Math.floor(originalVertexCount * targetRatio));

    // Compute bounding box for grid
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox!;
    const size = new THREE.Vector3();
    bbox.getSize(size);

    // Calculate grid resolution based on target
    const gridResolution = Math.ceil(Math.cbrt(targetVertexCount));
    const cellSize = new THREE.Vector3(
      size.x / gridResolution,
      size.y / gridResolution,
      size.z / gridResolution
    );

    // Vertex clustering
    const clusters: Map<string, {
      positions: number[];
      count: number;
      centroid: THREE.Vector3;
    }> = new Map();

    // Cluster vertices
    for (let i = 0; i < originalVertexCount; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const z = position.getZ(i);

      // Calculate cell index
      const cx = Math.floor((x - bbox.min.x) / cellSize.x);
      const cy = Math.floor((y - bbox.min.y) / cellSize.y);
      const cz = Math.floor((z - bbox.min.z) / cellSize.z);
      const key = `${cx},${cy},${cz}`;

      if (!clusters.has(key)) {
        clusters.set(key, {
          positions: [],
          count: 0,
          centroid: new THREE.Vector3()
        });
      }

      const cluster = clusters.get(key)!;
      cluster.positions.push(x, y, z);
      cluster.centroid.add(this.tempVec1.set(x, y, z));
      cluster.count++;
    }

    // Calculate centroids and create new vertices
    const newPositions: number[] = [];
    const vertexMap: Map<string, number> = new Map();
    let newVertexIndex = 0;

    for (const [key, cluster] of clusters) {
      cluster.centroid.divideScalar(cluster.count);
      newPositions.push(
        cluster.centroid.x,
        cluster.centroid.y,
        cluster.centroid.z
      );
      vertexMap.set(key, newVertexIndex);
      newVertexIndex++;
    }

    // Rebuild indices
    const newIndices: number[] = [];
    const indices = geometry.getIndex();

    if (indices) {
      for (let i = 0; i < indices.count; i += 3) {
        const a = indices.getX(i);
        const b = indices.getX(i + 1);
        const c = indices.getX(i + 2);

        const keyA = this.getClusterKey(position, a, bbox.min, cellSize);
        const keyB = this.getClusterKey(position, b, bbox.min, cellSize);
        const keyC = this.getClusterKey(position, c, bbox.min, cellSize);

        const newA = vertexMap.get(keyA)!;
        const newB = vertexMap.get(keyB)!;
        const newC = vertexMap.get(keyC)!;

        // Skip degenerate triangles
        if (newA !== newB && newB !== newC && newA !== newC) {
          newIndices.push(newA, newB, newC);
        }
      }
    }

    // Create new geometry
    const decimated = new THREE.BufferGeometry();
    decimated.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(newPositions, 3)
    );

    if (newIndices.length > 0) {
      decimated.setIndex(newIndices);
    }

    decimated.computeVertexNormals();
    decimated.computeBoundingSphere();

    return decimated;
  }

  private getClusterKey(
    position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    index: number,
    min: THREE.Vector3,
    cellSize: THREE.Vector3
  ): string {
    const x = position.getX(index);
    const y = position.getY(index);
    const z = position.getZ(index);

    const cx = Math.floor((x - min.x) / cellSize.x);
    const cy = Math.floor((y - min.y) / cellSize.y);
    const cz = Math.floor((z - min.z) / cellSize.z);

    return `${cx},${cy},${cz}`;
  }
}

// ==================== STREAMING LOADER ====================

/**
 * Streams geometry data progressively
 */
export class FikoStreamingGeometryLoader {
  private loadQueue: StreamingGeometryChunk[] = [];
  private isLoading: boolean = false;
  private maxConcurrentLoads: number = 3;
  private activeLoads: number = 0;

  // Callbacks
  private onChunkLoaded: ((chunk: StreamingGeometryChunk) => void) | null = null;
  private onProgress: ((loaded: number, total: number) => void) | null = null;

  // Statistics
  private stats = {
    chunksLoaded: 0,
    bytesLoaded: 0,
    averageLoadTime: 0
  };

  /**
   * Queue chunk for loading
   */
  queueChunk(chunk: StreamingGeometryChunk): void {
    // Insert sorted by priority
    const insertIndex = this.loadQueue.findIndex(c => c.priority < chunk.priority);
    if (insertIndex === -1) {
      this.loadQueue.push(chunk);
    } else {
      this.loadQueue.splice(insertIndex, 0, chunk);
    }

    this.processQueue();
  }

  /**
   * Update chunk priority
   */
  updatePriority(chunkId: string, newPriority: number): void {
    const chunk = this.loadQueue.find(c => c.id === chunkId);
    if (chunk) {
      chunk.priority = newPriority;
      // Re-sort queue
      this.loadQueue.sort((a, b) => b.priority - a.priority);
    }
  }

  /**
   * Cancel chunk loading
   */
  cancelChunk(chunkId: string): void {
    const index = this.loadQueue.findIndex(c => c.id === chunkId);
    if (index !== -1) {
      this.loadQueue.splice(index, 1);
    }
  }

  /**
   * Process load queue
   */
  private processQueue(): void {
    while (
      this.activeLoads < this.maxConcurrentLoads &&
      this.loadQueue.length > 0
    ) {
      const chunk = this.loadQueue.shift()!;
      this.loadChunk(chunk);
    }
  }

  /**
   * Load single chunk
   */
  private async loadChunk(chunk: StreamingGeometryChunk): Promise<void> {
    if (chunk.state !== 'pending') return;

    chunk.state = 'loading';
    this.activeLoads++;

    const startTime = performance.now();

    try {
      // Simulate async loading (in production, would load from network/disk)
      await new Promise(resolve => setTimeout(resolve, 10));

      chunk.state = 'loaded';
      chunk.lastUsedTime = performance.now();

      // Update stats
      const loadTime = performance.now() - startTime;
      this.stats.chunksLoaded++;
      this.stats.bytesLoaded += chunk.memoryBytes;
      this.stats.averageLoadTime =
        (this.stats.averageLoadTime * (this.stats.chunksLoaded - 1) + loadTime) /
        this.stats.chunksLoaded;

      if (this.onChunkLoaded) {
        this.onChunkLoaded(chunk);
      }
    } catch (error) {
      chunk.state = 'pending'; // Allow retry
    }

    this.activeLoads--;
    this.processQueue();
  }

  /**
   * Set callbacks
   */
  setCallbacks(
    onChunkLoaded: (chunk: StreamingGeometryChunk) => void,
    onProgress?: (loaded: number, total: number) => void
  ): void {
    this.onChunkLoaded = onChunkLoaded;
    this.onProgress = onProgress || null;
  }

  /**
   * Get statistics
   */
  getStats(): typeof this.stats & {
    queueLength: number;
    activeLoads: number;
  } {
    return {
      ...this.stats,
      queueLength: this.loadQueue.length,
      activeLoads: this.activeLoads
    };
  }

  /**
   * Clear queue
   */
  clearQueue(): void {
    this.loadQueue = [];
  }
}

// ==================== LOD MANAGER ====================

/**
 * Manages LOD levels for streaming geometry
 */
export class FikoStreamingLODManager {
  private decimator: FikoGeometryDecimator;
  private loader: FikoStreamingGeometryLoader;

  // Object states
  private objects: Map<number, StreamingObjectState> = new Map();

  // LOD configuration
  private lodDistances: number[] = [0, 50, 150, 400, 1000, 2500];
  private lodRatios: number[] = [1.0, 0.5, 0.25, 0.1, 0.02, 0.005];

  // Memory management
  private memoryBudget: MemoryBudget = {
    maxBytes: 512 * 1024 * 1024, // 512 MB
    usedBytes: 0,
    reservedBytes: 0,
    peakBytes: 0
  };

  // Transition settings
  private transitionSpeed: number = 5.0; // Units per second
  private enableTransitions: boolean = true;

  // Camera reference
  private camera: THREE.Camera | null = null;

  // Pre-allocated
  private tempSphere: THREE.Sphere = new THREE.Sphere();
  private tempVec: THREE.Vector3 = new THREE.Vector3();

  constructor() {
    this.decimator = new FikoGeometryDecimator();
    this.loader = new FikoStreamingGeometryLoader();

    this.loader.setCallbacks(
      (chunk) => this.onChunkLoaded(chunk)
    );
  }

  /**
   * Set camera for distance calculations
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /**
   * Register object for LOD management
   */
  registerObject(
    objectId: number,
    baseGeometry: THREE.BufferGeometry,
    boundingSphere?: THREE.Sphere
  ): void {
    // Compute bounding sphere if not provided
    if (!boundingSphere) {
      baseGeometry.computeBoundingSphere();
      boundingSphere = baseGeometry.boundingSphere!.clone();
    }

    // Create LOD levels
    const lodLevels: LODLevel[] = this.lodDistances.map((dist, i) => ({
      level: i,
      distanceThreshold: dist,
      triangleRatio: this.lodRatios[i],
      geometry: i === 0 ? baseGeometry : null, // Only LOD 0 has geometry initially
      isLoaded: i === 0,
      isLoading: false
    }));

    // Create object state
    const state: StreamingObjectState = {
      objectId,
      lodLevels,
      currentLOD: 0,
      targetLOD: 0,
      transitionProgress: 1.0,
      boundingSphere: boundingSphere.clone(),
      distanceToCamera: 0,
      screenSpaceSize: 0,
      priority: 0
    };

    this.objects.set(objectId, state);

    // Track memory
    const memBytes = this.estimateGeometryMemory(baseGeometry);
    this.memoryBudget.usedBytes += memBytes;
    this.memoryBudget.peakBytes = Math.max(
      this.memoryBudget.peakBytes,
      this.memoryBudget.usedBytes
    );
  }

  /**
   * Unregister object
   */
  unregisterObject(objectId: number): void {
    const state = this.objects.get(objectId);
    if (!state) return;

    // Free memory from LOD geometries
    for (const lod of state.lodLevels) {
      if (lod.geometry && lod.level > 0) {
        const memBytes = this.estimateGeometryMemory(lod.geometry);
        this.memoryBudget.usedBytes -= memBytes;
        lod.geometry.dispose();
      }
    }

    this.objects.delete(objectId);
  }

  /**
   * Update all LODs based on camera
   */
  update(deltaTime: number): void {
    if (!this.camera) return;

    const cameraPos = this.camera.position;

    for (const [id, state] of this.objects) {
      // Calculate distance to camera
      state.distanceToCamera = state.boundingSphere.center.distanceTo(cameraPos);

      // Calculate screen space size (approximate)
      const perspective = this.camera as THREE.PerspectiveCamera;
      if (perspective.fov) {
        const fovRad = (perspective.fov * Math.PI) / 180;
        state.screenSpaceSize =
          (state.boundingSphere.radius * 2) /
          (state.distanceToCamera * Math.tan(fovRad / 2));
      }

      // Determine target LOD level
      const targetLOD = this.calculateTargetLOD(state);
      state.targetLOD = targetLOD;

      // Update priority for loading
      state.priority = this.calculatePriority(state);

      // Handle LOD transition
      if (state.currentLOD !== state.targetLOD) {
        this.handleLODTransition(state, deltaTime);
      }

      // Request LOD geometry if needed
      this.ensureLODLoaded(state);
    }

    // Memory management - evict unused LODs if over budget
    if (this.memoryBudget.usedBytes > this.memoryBudget.maxBytes * 0.9) {
      this.evictUnusedLODs();
    }
  }

  /**
   * Calculate target LOD level for object
   */
  private calculateTargetLOD(state: StreamingObjectState): number {
    // Find appropriate LOD based on distance
    for (let i = this.lodDistances.length - 1; i >= 0; i--) {
      if (state.distanceToCamera >= this.lodDistances[i]) {
        return i;
      }
    }
    return 0;
  }

  /**
   * Calculate loading priority
   */
  private calculatePriority(state: StreamingObjectState): number {
    // Higher priority for:
    // - Closer objects
    // - Larger screen space size
    // - Objects needing LOD change

    const distanceFactor = 1.0 / Math.max(state.distanceToCamera, 1);
    const sizeFactor = state.screenSpaceSize;
    const lodNeedFactor = state.currentLOD !== state.targetLOD ? 2.0 : 1.0;

    return distanceFactor * sizeFactor * lodNeedFactor * 1000;
  }

  /**
   * Handle LOD level transition
   */
  private handleLODTransition(state: StreamingObjectState, deltaTime: number): void {
    const targetLOD = state.lodLevels[state.targetLOD];

    // Check if target LOD is loaded
    if (!targetLOD.isLoaded) {
      return; // Wait for loading
    }

    if (this.enableTransitions) {
      // Smooth transition
      const transitionDelta = this.transitionSpeed * deltaTime;

      if (state.currentLOD < state.targetLOD) {
        state.transitionProgress -= transitionDelta;
        if (state.transitionProgress <= 0) {
          state.currentLOD++;
          state.transitionProgress = 1.0;
        }
      } else {
        state.transitionProgress -= transitionDelta;
        if (state.transitionProgress <= 0) {
          state.currentLOD--;
          state.transitionProgress = 1.0;
        }
      }
    } else {
      // Instant transition
      state.currentLOD = state.targetLOD;
      state.transitionProgress = 1.0;
    }
  }

  /**
   * Ensure required LOD is loaded
   */
  private ensureLODLoaded(state: StreamingObjectState): void {
    const targetLOD = state.lodLevels[state.targetLOD];

    if (!targetLOD.isLoaded && !targetLOD.isLoading) {
      // Request loading
      targetLOD.isLoading = true;

      // Get base geometry for decimation
      const baseLOD = state.lodLevels[0];
      if (!baseLOD.geometry) return;

      // Queue chunk for loading
      const chunk: StreamingGeometryChunk = {
        id: `${state.objectId}_lod${state.targetLOD}`,
        lodLevel: state.targetLOD,
        triangleCount: 0,
        boundingBox: new THREE.Box3(),
        state: 'pending',
        geometry: null,
        priority: state.priority,
        lastUsedTime: 0,
        memoryBytes: 0
      };

      // Generate decimated geometry (could be async in production)
      const decimated = this.decimator.decimate(
        baseLOD.geometry,
        targetLOD.triangleRatio
      );

      chunk.geometry = decimated;
      chunk.triangleCount = decimated.index
        ? decimated.index.count / 3
        : decimated.getAttribute('position').count / 3;
      chunk.memoryBytes = this.estimateGeometryMemory(decimated);
      chunk.state = 'loaded';

      this.onChunkLoaded(chunk);
    }
  }

  /**
   * Handle loaded chunk
   */
  private onChunkLoaded(chunk: StreamingGeometryChunk): void {
    // Parse chunk ID
    const parts = chunk.id.split('_lod');
    if (parts.length !== 2) return;

    const objectId = parseInt(parts[0]);
    const lodLevel = parseInt(parts[1]);

    const state = this.objects.get(objectId);
    if (!state) return;

    const lod = state.lodLevels[lodLevel];
    if (!lod) return;

    // Store geometry
    lod.geometry = chunk.geometry;
    lod.isLoaded = true;
    lod.isLoading = false;

    // Track memory
    this.memoryBudget.usedBytes += chunk.memoryBytes;
    this.memoryBudget.peakBytes = Math.max(
      this.memoryBudget.peakBytes,
      this.memoryBudget.usedBytes
    );
  }

  /**
   * Evict unused LOD levels to free memory
   */
  private evictUnusedLODs(): void {
    const now = performance.now();
    const evictionThreshold = 10000; // 10 seconds unused

    const evictionCandidates: Array<{
      objectId: number;
      lodLevel: number;
      lastUsed: number;
      memBytes: number;
    }> = [];

    for (const [id, state] of this.objects) {
      for (let i = 1; i < state.lodLevels.length; i++) {
        const lod = state.lodLevels[i];
        if (
          lod.isLoaded &&
          lod.geometry &&
          i !== state.currentLOD &&
          i !== state.targetLOD
        ) {
          evictionCandidates.push({
            objectId: id,
            lodLevel: i,
            lastUsed: 0, // Would track actual usage in production
            memBytes: this.estimateGeometryMemory(lod.geometry)
          });
        }
      }
    }

    // Sort by last used (oldest first)
    evictionCandidates.sort((a, b) => a.lastUsed - b.lastUsed);

    // Evict until under budget
    for (const candidate of evictionCandidates) {
      if (this.memoryBudget.usedBytes <= this.memoryBudget.maxBytes * 0.7) {
        break;
      }

      const state = this.objects.get(candidate.objectId);
      if (!state) continue;

      const lod = state.lodLevels[candidate.lodLevel];
      if (lod.geometry) {
        lod.geometry.dispose();
        lod.geometry = null;
        lod.isLoaded = false;
        this.memoryBudget.usedBytes -= candidate.memBytes;
      }
    }
  }

  /**
   * Estimate memory usage of geometry
   */
  private estimateGeometryMemory(geometry: THREE.BufferGeometry): number {
    let bytes = 0;

    for (const name in geometry.attributes) {
      const attr = geometry.getAttribute(name);
      bytes += attr.array.byteLength;
    }

    if (geometry.index) {
      bytes += geometry.index.array.byteLength;
    }

    return bytes;
  }

  /**
   * Get current LOD geometry for object
   */
  getCurrentLODGeometry(objectId: number): THREE.BufferGeometry | null {
    const state = this.objects.get(objectId);
    if (!state) return null;

    const lod = state.lodLevels[state.currentLOD];
    return lod?.geometry || null;
  }

  /**
   * Get transition info for blending
   */
  getTransitionInfo(objectId: number): {
    currentLOD: number;
    targetLOD: number;
    progress: number;
  } | null {
    const state = this.objects.get(objectId);
    if (!state) return null;

    return {
      currentLOD: state.currentLOD,
      targetLOD: state.targetLOD,
      progress: state.transitionProgress
    };
  }

  /**
   * Set memory budget
   */
  setMemoryBudget(maxBytes: number): void {
    this.memoryBudget.maxBytes = maxBytes;
  }

  /**
   * Get statistics
   */
  getStats(): {
    objectCount: number;
    memoryUsed: string;
    memoryBudget: string;
    memoryUtilization: number;
    loaderStats: ReturnType<FikoStreamingGeometryLoader['getStats']>;
  } {
    return {
      objectCount: this.objects.size,
      memoryUsed: `${(this.memoryBudget.usedBytes / 1024 / 1024).toFixed(2)} MB`,
      memoryBudget: `${(this.memoryBudget.maxBytes / 1024 / 1024).toFixed(2)} MB`,
      memoryUtilization: (this.memoryBudget.usedBytes / this.memoryBudget.maxBytes) * 100,
      loaderStats: this.loader.getStats()
    };
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    for (const [id] of this.objects) {
      this.unregisterObject(id);
    }
    this.objects.clear();
    this.loader.clearQueue();
  }
}

// ==================== PROGRESSIVE MESH VIEWER ====================

/**
 * Progressive mesh viewer with streaming LOD
 * Shows placeholder immediately, loads detail progressively
 */
export class FikoProgressiveMeshViewer {
  private lodManager: FikoStreamingLODManager;
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  // Managed meshes
  private meshes: Map<number, THREE.Mesh> = new Map();
  private placeholders: Map<number, THREE.Mesh> = new Map();

  // Placeholder material (simple, fast)
  private placeholderMaterial: THREE.MeshBasicMaterial;

  // Loading state
  private isInitialLoadComplete: boolean = false;

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
    this.lodManager = new FikoStreamingLODManager();
    this.lodManager.setCamera(camera);

    // Simple placeholder material
    this.placeholderMaterial = new THREE.MeshBasicMaterial({
      color: 0xcccccc,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
  }

  /**
   * Add mesh with progressive loading
   */
  async addMesh(
    objectId: number,
    geometry: THREE.BufferGeometry,
    material: THREE.Material
  ): Promise<void> {
    // Create placeholder immediately (bounding box mesh)
    const placeholder = this.createPlaceholder(geometry);
    this.placeholders.set(objectId, placeholder);
    this.scene.add(placeholder);

    // Create actual mesh but hidden initially
    const mesh = new THREE.Mesh(geometry, material);
    mesh.visible = false;
    this.meshes.set(objectId, mesh);
    this.scene.add(mesh);

    // Register with LOD manager
    this.lodManager.registerObject(objectId, geometry);

    // Show mesh, hide placeholder after short delay
    // This gives LOD system time to prepare
    await new Promise(resolve => setTimeout(resolve, 50));

    mesh.visible = true;
    placeholder.visible = false;
  }

  /**
   * Create placeholder mesh (fast bounding box)
   */
  private createPlaceholder(geometry: THREE.BufferGeometry): THREE.Mesh {
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox!;
    const size = new THREE.Vector3();
    bbox.getSize(size);

    const boxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    const placeholder = new THREE.Mesh(boxGeometry, this.placeholderMaterial);
    placeholder.position.copy(center);

    return placeholder;
  }

  /**
   * Remove mesh
   */
  removeMesh(objectId: number): void {
    const mesh = this.meshes.get(objectId);
    if (mesh) {
      this.scene.remove(mesh);
      this.meshes.delete(objectId);
    }

    const placeholder = this.placeholders.get(objectId);
    if (placeholder) {
      this.scene.remove(placeholder);
      placeholder.geometry.dispose();
      this.placeholders.delete(objectId);
    }

    this.lodManager.unregisterObject(objectId);
  }

  /**
   * Update progressive loading
   */
  update(deltaTime: number): void {
    this.lodManager.update(deltaTime);

    // Update mesh geometries with current LOD
    for (const [id, mesh] of this.meshes) {
      const lodGeometry = this.lodManager.getCurrentLODGeometry(id);
      if (lodGeometry && mesh.geometry !== lodGeometry) {
        mesh.geometry = lodGeometry;
      }
    }
  }

  /**
   * Get statistics
   */
  getStats(): ReturnType<FikoStreamingLODManager['getStats']> & {
    meshCount: number;
    placeholderCount: number;
  } {
    return {
      ...this.lodManager.getStats(),
      meshCount: this.meshes.size,
      placeholderCount: this.placeholders.size
    };
  }

  /**
   * Dispose
   */
  dispose(): void {
    for (const [id] of this.meshes) {
      this.removeMesh(id);
    }
    this.lodManager.dispose();
    this.placeholderMaterial.dispose();
  }
}

// Export
export default FikoStreamingLODManager;
